// Modules
import { useEffect, useRef } from 'react';
import cn from 'classnames';

// Types
import type { ChangeEvent, RefObject } from 'react';
import type { TSelectOption, TSelectOptionProps } from './Select.type';

// Styles
import styles from './styles/SelectOption.module.scss';

// Components
import Icon from '../Icon';

// Helpers
import { isEmpty } from '../../helpers';

// Constants
import { SELECT_CLEAR_SEARCH_ID, SELECT_OPTION_DEFAULT_PROPS } from './Select.constant';

const SelectOption = (props: TSelectOptionProps) => {
	const {
		activeOption,
		className,
		isOpen,
		onChangeSearch,
		onClearSearch,
		onClick,
		optionClassName,
		options,
		ref,
		searchPlaceholder,
		searchValue,
		searchable,
		size = SELECT_OPTION_DEFAULT_PROPS.size,
		...restProps
	} = props

	const localRef = useRef<HTMLDivElement | null>(null);
	const selectOptionRef = ref as RefObject<HTMLDivElement> || localRef;

	const selectOptionCN = cn(styles.legion_select_option, className, styles[size!], {
		[styles.open]: isOpen,
		[styles.close]: !isOpen,
	});
	const inputSearchCN = cn(styles.search_input);

	useEffect(() => {
		if (isOpen && selectOptionRef?.current) {
			selectOptionRef.current!.scrollTo(0, 0);
		}
	}, [isOpen])

	const handleClick = (selected: TSelectOption) => () => {
		onClick?.(selected);
	}

	const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
		onChangeSearch?.(e.currentTarget.value, e)
	}

	const handleClearSearch = () => {
		onChangeSearch?.('')
		onClearSearch?.('')
	}

	return (
		<div ref={selectOptionRef} className={selectOptionCN} {...restProps}>
			{searchable && (
				<div className={styles.search_wrapper}>
					<input
						tabIndex={-1}
						className={inputSearchCN}
						placeholder={searchPlaceholder}
						value={searchValue}
						onChange={handleChangeSearch}
					/>
					<svg
						xmlns={'http://www.w3.org/2000/svg'}
					>
						<rect height={'100%'} width={'100%'} fill={'none'}/>
					</svg>
					{!isEmpty(searchValue) && (

						<Icon
							id={SELECT_CLEAR_SEARCH_ID}
							className={styles.search_icon}
							icon={'close-circle'}
							onClick={handleClearSearch}
						/>
					)}
				</div>
			)}
			{options.map((option, index) => {
				const { value, label } = option;
				const optionCN = cn(styles.option, optionClassName, {
					[styles.active]: Array.isArray(activeOption) ? activeOption.includes(value) : activeOption === value,
				});

				return (
					<div key={index} className={optionCN} onClick={handleClick(option)}>
						{label}
					</div>
				)
			})}
		</div>
	)
}

export default SelectOption;
